/**
 * OrbitPrint — Print a file: file → location → printer → copies → send → live tracker.
 * The tracker shows the server's own answer to "where am I": next up, 3rd in
 * line while a print runs, waiting for approval, printing page N, printed,
 * failed with the printer's reason. Live over the socket (slice 5): a "this job
 * changed" hint re-fetches it; the 3 s poll is the fallback (30 s while live).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { orbitApi, errText } from '../OrbitAuthContext';
import { usePlan, docsLeft, fmtDay } from '../plan';
import { useLiveRefresh, usePollMs } from '../live';
import { OrbitButton, Lamp, PrinterIcon } from './OrbitShell';
import { RingGauge } from './LocationCard';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp,.gif,.bmp';
const MAX_MB = 50;
const POLL_LIVE_MS = 30000;      // the socket says when this job moved; the poll is the fallback
const POLL_FALLBACK_MS = 3000;
const FINAL = ['printed', 'failed', 'declined', 'cancelled', 'expired'];

function fmtSize(b) { return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`; }

const LAMP_FOR = { pending: 'ready', held: 'error', claimed: 'busy', printed: 'ready', failed: 'alarm', declined: 'alarm', cancelled: 'offline', expired: 'offline' };
const LABEL_FOR = { pending: 'In line', held: 'Held', claimed: 'Printing', printed: 'Printed', failed: 'Failed', declined: 'Declined', cancelled: 'Cancelled', expired: 'Expired' };

export function JobLamp({ status }) {
  return <Lamp state={LAMP_FOR[status] || 'offline'}>{LABEL_FOR[status] || status}</Lamp>;
}

export default function PrintFileDialog({ open, onClose, locations, onSent }) {
  const [file, setFile] = useState(null);
  const [locId, setLocId] = useState('');
  const [printerId, setPrinterId] = useState('');
  const [copies, setCopies] = useState(1);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [job, setJob] = useState(null);       // tracker
  const inputRef = useRef(null);

  const usable = useMemo(() => (locations || []).filter((l) => l.status === 'active'), [locations]);
  const loc = usable.find((l) => l.id === locId);
  const printers = (loc?.printers || []).filter((p) => p.present && p.shared !== false);
  const printer = printers.find((p) => p.id === printerId);
  // the plan picture (slice 4): paused location / printer = the Send button is faded with the reason, never live-but-dead
  const { plan: planInfo, refresh: refreshPlan } = usePlan();
  const locPaused = !!(loc && (loc.paused_by_plan || loc.paused_by_user));
  const blocked = locPaused || !!printer?.paused_by_plan;
  const left = docsLeft(planInfo);

  useEffect(() => {
    if (!open) return;
    setFile(null); setCopies(1); setJob(null); setBusy(false);
    const firstOnline = usable.find((l) => l.online) || usable[0];
    setLocId(firstOnline?.id || '');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // pick the best printer when the location changes: ready first, then any present
    const ps = (usable.find((l) => l.id === locId)?.printers || []).filter((p) => p.present && p.shared !== false);
    const best = ps.find((p) => p.status === 'ready') || ps[0];
    setPrinterId(best?.id || '');
  }, [locId, usable]);

  const poll = useCallback(async (id) => {
    try { const r = await orbitApi.get(`/jobs/${id}`); setJob(r.data); }
    catch { /* keep the last state; the next tick retries */ }
  }, []);

  const pollMs = usePollMs(POLL_LIVE_MS, POLL_FALLBACK_MS);
  const jobId = job?.id;
  const jobActive = !!job && !FINAL.includes(job.status);
  useEffect(() => {
    // fallback poll while the job is still moving; the socket hint below is the fast path
    if (!jobId || !jobActive) return undefined;
    const t = setInterval(() => poll(jobId), pollMs);
    return () => clearInterval(t);
  }, [jobId, jobActive, pollMs, poll]);
  useLiveRefresh('jobs', () => { if (jobId) poll(jobId); }, (msg) => !msg.job_id || msg.job_id === jobId);

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`That file is too big. The limit is ${MAX_MB} MB.`); return; }
    setFile(f);
  };

  const send = async () => {
    if (!file || !printer) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('printer_id', printer.id); fd.append('copies', String(copies)); fd.append('title', file.name);
    try {
      const r = await orbitApi.post('/jobs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setJob({ id: r.data.job_id, status: r.data.status, held_reason: r.data.held_reason, message: r.data.message,
               queue_position: r.data.queue_position, printer_busy: r.data.printer_busy, location_online: r.data.location_online,
               title: file.name, printer_name: printer.name, location_name: loc.name });
      onSent?.();
      refreshPlan();       // the badge and "documents left" move at once
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!job) return;
    try { const r = await orbitApi.post(`/jobs/${job.id}/cancel`); toast.success(r.data.message); poll(job.id); onSent?.(); }
    catch (err) { toast.error(errText(err)); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true" aria-label="Print a file"
      style={{ background: 'rgba(6,12,24,.72)', backdropFilter: 'blur(6px)' }} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="orbit-root orbit-card is-kit w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 sm:p-8" style={{ minHeight: 'auto', background: 'var(--ob-surface)' }} data-testid="orbit-print-dialog">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="orbit-label m-0">Print a file</p>
            <h2 className="orbit-display mt-1" style={{ fontSize: 32 }}>{job ? job.title : 'Choose a file, a location, a printer.'}</h2>
          </div>
          <button className="orbit-link is-muted text-sm" onClick={onClose} disabled={busy} data-testid="orbit-print-close">Close</button>
        </div>

        {!job ? (
          <div className="mt-6 grid gap-6">
            {/* 1. file */}
            <div>
              <p className="orbit-label mb-2">1 · File</p>
              <div className={`orbit-panel p-5 text-center cursor-pointer ${drag ? 'ring-2' : ''}`} style={{ borderStyle: 'dashed', borderColor: drag ? 'var(--ob-green)' : undefined }}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()} data-testid="orbit-print-drop">
                <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="orbit-print-file-input" />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <span className="font-semibold">{file.name}</span>
                    <span className="orbit-mono text-xs orbit-muted">{fmtSize(file.size)}</span>
                    <span className="orbit-link text-xs">Change</span>
                  </div>
                ) : (
                  <>
                    <p className="font-semibold m-0">Drop a PDF or image here, or click to choose</p>
                    <p className="text-xs orbit-muted mt-1 m-0">PDF, JPG, PNG, TIFF, WEBP, GIF, BMP · up to {MAX_MB} MB. Word and Excel come with the Windows app.</p>
                  </>
                )}
              </div>
            </div>

            {/* 2. location */}
            <div>
              <p className="orbit-label mb-2">2 · Location</p>
              <div className="flex flex-wrap gap-2" data-testid="orbit-print-locations">
                {usable.map((l) => {
                  const lp = l.paused_by_plan || l.paused_by_user;
                  return (
                    <button key={l.id} type="button" onClick={() => setLocId(l.id)}
                      className={`orbit-btn is-small ${l.id === locId ? 'is-secondary' : 'is-quiet'} ${lp ? 'opacity-60' : ''}`} style={{ textTransform: 'none', letterSpacing: 0, fontFamily: 'var(--ob-body)', fontWeight: 600 }}
                      title={lp ? 'Paused — cannot receive documents right now' : undefined} data-testid={`orbit-print-loc-${l.id}`}>
                      <span className={`orbit-dot ${l.online && !lp ? '' : 'is-off'}`} />{l.name}
                      {lp ? <span className="orbit-muted text-xs"> · paused</span> : !l.online && <span className="orbit-muted text-xs"> · offline</span>}
                    </button>
                  );
                })}
              </div>
              {loc && locPaused && (
                <p className="text-xs mt-2 m-0" style={{ color: 'var(--ob-amber)' }} data-testid="orbit-print-loc-paused">
                  {loc.paused_by_user ? `${loc.name} is paused by choice. A manager can make it active from its card.`
                    : `${loc.name} is paused on the ${planInfo?.plan_label || 'current'} plan. A manager can make it active from its card (another location pauses instead), or upgrade.`}
                </p>
              )}
              {loc && !locPaused && !loc.online && <p className="text-xs mt-2 m-0" style={{ color: 'var(--ob-amber)' }}>{loc.name} is offline. Your document will wait and print when it comes back.</p>}
            </div>

            {/* 3. printer */}
            <div>
              <p className="orbit-label mb-2">3 · Printer</p>
              {printers.length === 0 ? (
                <p className="text-sm orbit-ink2 m-0">No printers are shared at this location yet.</p>
              ) : (
                <ul className="list-none p-0 m-0 orbit-panel divide-y" style={{ borderColor: 'var(--ob-rule)' }} data-testid="orbit-print-printers">
                  {printers.map((p) => {
                    const st = loc.online ? p.status : 'offline';
                    return (
                      <li key={p.id}>
                        <button type="button" onClick={() => setPrinterId(p.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left"
                          style={{ background: p.id === printerId ? 'rgba(46,212,122,.08)' : 'transparent', border: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}
                          data-testid={`orbit-print-printer-${p.id}`}>
                          <span className="grid place-items-center h-5 w-5 rounded-full" style={{ border: `2px solid ${p.id === printerId ? 'var(--ob-green)' : 'var(--ob-rule-2)'}` }}>
                            {p.id === printerId && <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'var(--ob-green)' }} />}
                          </span>
                          <RingGauge status={st} />
                          <span className="flex-1 min-w-0">
                            <span className="block font-semibold text-[15px]">{p.name}</span>
                            <span className="block orbit-mono text-[11px] orbit-muted truncate">{p.windows_queue_name}{p.has_preset ? ' · preset saved' : ''}{p.needs_approval ? ' · needs approval' : ''}</span>
                          </span>
                          <Lamp state={{ ready: 'ready', printing: 'busy', attention: 'error', offline: 'offline', unknown: 'offline' }[st] || 'offline'}>
                            {{ ready: 'Ready', printing: 'Printing', attention: 'Attention', offline: 'Offline', unknown: 'Unknown' }[st]}
                          </Lamp>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {printer?.paused_by_plan && !locPaused && <p className="text-xs mt-2 m-0" style={{ color: 'var(--ob-amber)' }} data-testid="orbit-print-printer-paused">This printer is paused on the {planInfo?.plan_label || 'current'} plan — it includes {planInfo?.limits?.printers ?? 'fewer'} printers. Pick another, or upgrade.</p>}
              {printer?.needs_approval && !printer?.paused_by_plan && <p className="text-xs mt-2 m-0" style={{ color: 'var(--ob-amber)' }}>This printer needs a manager's approval for you. Your document will wait until someone approves it.</p>}
            </div>

            {/* 4. copies + send */}
            <div className="flex flex-wrap items-end justify-between gap-4 pt-2" style={{ borderTop: '1px solid var(--ob-rule)' }}>
              <label className="orbit-field">
                <span className="orbit-label">Copies</span>
                <input type="number" min={1} max={20} value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(20, parseInt(e.target.value || '1', 10))))}
                  className="orbit-input !w-24" data-testid="orbit-print-copies" />
                {left !== null && (
                  <span className="text-xs mt-1" style={{ color: left < copies ? 'var(--ob-amber)' : 'var(--ob-mute)' }} data-testid="orbit-print-docs-left">
                    {left === 0 ? `Monthly quota of ${planInfo.limit} used — resets ${fmtDay(planInfo.resets_on)}.`
                      : `${left} of ${planInfo.limit} documents left this month · copies count`}
                  </span>
                )}
              </label>
              <div title={blocked ? 'This location or printer is paused' : undefined}>
                <OrbitButton onClick={send} busy={busy} disabled={!file || !printer || blocked} testid="orbit-print-send"><PrinterIcon /> Send to {printer ? printer.name : 'printer'}</OrbitButton>
              </div>
            </div>
          </div>
        ) : (
          /* tracker */
          <div className="mt-6" data-testid="orbit-print-tracker">
            <div className="orbit-panel p-5 flex items-start gap-4">
              <RingGauge status={job.status === 'claimed' ? 'printing' : job.status === 'printed' ? 'ready' : ['failed', 'declined'].includes(job.status) ? 'attention' : 'offline'} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3"><JobLamp status={job.status} /><span className="orbit-mono text-xs orbit-muted">{job.printer_name} · {job.location_name}</span></div>
                <p className="mt-3 text-[15px] leading-relaxed m-0" data-testid="orbit-print-message">{job.message}</p>
                {job.queue_position > 1 && <p className="orbit-mono text-xs orbit-muted mt-1 m-0">Position {job.queue_position} on this printer</p>}
                {job.location_online === false && job.status === 'pending' && <p className="text-xs mt-1 m-0" style={{ color: 'var(--ob-amber)' }}>The location is offline right now.</p>}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap justify-between gap-3">
              {['pending', 'held'].includes(job.status)
                ? <button className="orbit-link is-danger text-sm" onClick={cancel} data-testid="orbit-print-cancel">Cancel this job</button>
                : <span />}
              <div className="flex gap-3">
                <OrbitButton variant="quiet" size="small" onClick={() => setJob(null)} testid="orbit-print-another">Print another</OrbitButton>
                <OrbitButton size="small" onClick={onClose} testid="orbit-print-done">Done</OrbitButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
